import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Box, Chip, Container, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Paper, Stack, Typography } from "@mui/material";
import { useApplicationContext } from "ApplicationContext";
import { useDocumentTitle } from "Hooks/useDocumentTitle";
import { DocumentationFilter, EVersionStage, FileTypeClass, GetFileTypeData, GetFileTypeLatestHostSystemFileData, GetHostSystemData, LatestHostSystemFileDto, LatestVersionKey } from 'apiClient/Api';
import IconPlaceholder from 'Pages/Admin/IconPlaceholder';
import { Link } from 'react-router';
import { useTranslation } from "react-i18next";

/**
 * Builds latest host system file entries from platform configurations when latest data is missing.
 * @param fileType The file type data from public endpoints.
 * @returns {LatestHostSystemFileDto[]} The latest host system file entries.
 */
const buildLatestFilesFromPlatformConfigurations = (fileType: GetFileTypeData): LatestHostSystemFileDto[] => {
    if (!fileType.platformConfigurations) {
        return [];
    }

    return fileType.platformConfigurations.map((configuration) => ({
        hostSystemId: configuration.hostSystemId,
        hostSystemName: configuration.hostSystemName,
        fileId: null,
        versionKey: null,
    }));
};

/**
 * Normalizes public file types into the latest host system data format.
 * @param fileTypes The file types from public endpoints.
 * @returns {GetFileTypeLatestHostSystemFileData[]} Normalized file type data.
 */
const normalizePublicFileTypes = (fileTypes: GetFileTypeData[]): GetFileTypeLatestHostSystemFileData[] => {
    return fileTypes.map((fileType) => ({
        id: fileType.id,
        name: fileType.name,
        public: fileType.public,
        type: fileType.type,
        latestFiles: buildLatestFilesFromPlatformConfigurations(fileType),
        packageId: fileType.packageId ?? null,
        iconId: fileType.iconId ?? null,
        screenshotIds: fileType.screenshotIds ?? [],
    }));
};

/**
 * Supports API field name changes for latest files.
 */
type LatestFilesFallback = LatestHostSystemFileDto & {
    latestVersion?: string | null;
    version?: string | null;
    latestVersionKey?: LatestVersionKey | null;
    hostSystem?: string | null;
};

/**
 * Resolves latest files from the current or legacy API shape.
 * @param fileType The file type entry.
 * @returns {LatestFilesFallback[]} The resolved latest files.
 */
const resolveLatestFiles = (fileType: GetFileTypeLatestHostSystemFileData): LatestFilesFallback[] => {
    const legacy = (fileType as { latestHostSystemFiles?: LatestHostSystemFileDto[] }).latestHostSystemFiles;
    return (fileType.latestFiles ?? legacy ?? []) as LatestFilesFallback[];
};

/**
 * Applications component to display a list of applications.
 * @returns {JSX.Element} The rendered component.
 */
export const Applications = (): JSX.Element => {
    useDocumentTitle("base.pages.applications");
    const { t } = useTranslation();
    const [fileTypeData, setFileTypeData] = useState<GetFileTypeLatestHostSystemFileData[]>();
    const [selectedHostSystems, setSelectedHostSystems] = useState<string[]>([]);
    const [hostSystems, setHostSystems] = useState<GetHostSystemData[]>([]);
    const context = useApplicationContext();
    const controller = context.api.appStore;
    const hostSystemController = context.api.fileType;

    /**
     * Formats a latest version key into a display string.
     * @param versionKey The version key to format.
     * @returns {string} Formatted version text.
     */
    const formatVersionKey = (versionKey?: LatestVersionKey | null): string => {
        if (!versionKey) {
            return t("applications.versionUnknown");
        }

        const segments = [versionKey.major, versionKey.minor, versionKey.patch].filter(
            (segment) => segment !== undefined && segment !== null,
        ) as number[];

        if (segments.length === 0) {
            return t("applications.versionUnknown");
        }

        const version = segments.join(".");
        return t("version.item.versionNumber", { version });
    };

    /**
     * Builds a display line for the latest file entry.
     * @param latestFile The latest host system file entry.
     * @returns {string} Display text containing host system and version.
     */
    const formatLatestFileLine = (latestFile: LatestFilesFallback): string => {
        const hostSystem = latestFile.hostSystemName ?? latestFile.hostSystem ?? t("version.item.hostSystemUnknown");
        const fallbackVersion = latestFile.latestVersion ?? latestFile.version ?? null;
        const version = fallbackVersion ? t("version.item.versionNumber", { version: fallbackVersion }) : formatVersionKey(latestFile.versionKey ?? latestFile.latestVersionKey);
        const stageLabel = getStageLabel(latestFile.stage);
        const baseLabel = t("applications.latestVersionLine", { hostSystem, version });
        return stageLabel ? `${baseLabel} • ${stageLabel}` : baseLabel;
    };

    /**
     * Resolves a translated stage label.
     * @param stage The version stage.
     * @returns {string} Stage label.
     */
    const getStageLabel = (stage?: EVersionStage): string => {
        switch (stage) {
            case EVersionStage.INTERNAL:
                return t("versionStage.internal");
            case EVersionStage.ALPHA:
                return t("versionStage.alpha");
            case EVersionStage.BETA:
                return t("versionStage.beta");
            case EVersionStage.RELEASE_CANDIDATE:
                return t("versionStage.release_candidate");
            case EVersionStage.RELEASE:
                return t("versionStage.release");
            case EVersionStage.APPROVED:
                return t("versionStage.approved");
            default:
                return "";
        }
    };

    /**
     * Builds available host system options from the host system endpoint.
     * @returns {{ id: string; name: string }[]} The host system options.
     */
    const availableHostSystems = useMemo(() => {
        return hostSystems
            .filter((system) => system.id)
            .map((system) => ({ id: system.id as string, name: system.name ?? system.id as string }));
    }, [hostSystems]);

    /**
     * Builds name lookup for host systems.
     * @returns {Record<string, string>} The name-to-id map.
     */
    const hostSystemNameToId = useMemo(() => {
        return availableHostSystems.reduce<Record<string, string>>((acc, system) => {
            if (system.name) {
                acc[system.name] = system.id;
            }
            return acc;
        }, {});
    }, [availableHostSystems]);

    /**
     * Resolves host system id for a latest file entry.
     * @param latestFile The latest host system file entry.
     * @returns {string} The resolved host system id.
     */
    const resolveHostSystemId = (latestFile: LatestFilesFallback): string => {
        if (latestFile.hostSystemId) {
            return latestFile.hostSystemId;
        }
        if (latestFile.hostSystemName && hostSystemNameToId[latestFile.hostSystemName]) {
            return hostSystemNameToId[latestFile.hostSystemName];
        }
        if (latestFile.hostSystem && hostSystemNameToId[latestFile.hostSystem]) {
            return hostSystemNameToId[latestFile.hostSystem];
        }
        return "";
    };

    /**
     * Toggles a host system filter chip.
     * @param systemId The host system id to toggle.
     */
    const toggleHostSystem = (systemId: string) => {
        setSelectedHostSystems((prev) => (prev.includes(systemId)
            ? prev.filter((value) => value !== systemId)
            : [...prev, systemId]
        ));
    };

    /**
     * Builds a query string for host system filters.
     * @param systemIds The selected host system IDs.
     * @returns {string} Query string starting with ? or empty string.
     */
    const buildHostSystemSearch = (systemIds: string[]): string => {
        if (systemIds.length === 0) {
            return "";
        }
        const params = new URLSearchParams();
        systemIds.forEach((id) => params.append("system", id));
        const query = params.toString();
        return query ? `?${query}` : "";
    };

    /**
     * Serializes query params with repeated array keys (e.g. system=1&system=2).
     * @param params The query params.
     * @returns {string} The serialized query string.
     */
    const serializeQueryParams = (params: Record<string, unknown>): string => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value === undefined || value === null) {
                return;
            }
            if (Array.isArray(value)) {
                value.forEach((entry) => {
                    if (entry === undefined || entry === null) {
                        return;
                    }
                    searchParams.append(key, String(entry));
                });
                return;
            }
            searchParams.append(key, String(value));
        });
        return searchParams.toString();
    };


    /**
     * Loads host systems from the dedicated endpoint.
     */
    const fetchHostSystems = useCallback(async () => {
        try {
            const response = await hostSystemController.fileTypeGetHostSystems();
            setHostSystems(response.data ?? []);
        } catch (error) {
            setHostSystems([]);
            context.apiError?.(error);
        }
    }, [hostSystemController, context]);

    /**
     * Loads applications with optional host system filtering.
     */
    const fetchFileTypes = useCallback(async () => {
        const query = {
            variant: FileTypeClass.APPLICATION,
            system: selectedHostSystems.length > 0 ? selectedHostSystems : undefined,
            documentationFilter: DocumentationFilter.ExcludeDocumentation,
        };
        try {
            const response = await controller.appStoreGetFileTypes(query, {
                paramsSerializer: {
                    serialize: (params: Record<string, unknown>) => serializeQueryParams(params),
                },
            });
            setFileTypeData(response.data ?? []);
        } catch (error) {
            if (!context.signInState) {
                try {
                    const publicResponse = await controller.appStoreGetFileTypesPublic({ variant: FileTypeClass.APPLICATION,documentationFilter: DocumentationFilter.ExcludeDocumentation });
                    const normalized = publicResponse.data ? normalizePublicFileTypes(publicResponse.data) : [];
                    setFileTypeData(normalized);
                } catch (publicError) {
                    context.apiError?.(publicError);
                    setFileTypeData([]);
                }
                return;
            }
            context.apiError?.(error);
            setFileTypeData([]);
        }
    }, [controller, context, selectedHostSystems]);

    useEffect(() => {
        fetchHostSystems();
    }, [fetchHostSystems]);

    useEffect(() => {
        fetchFileTypes();
    }, [fetchFileTypes]);

    return (
        <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
            <Paper variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
                <Typography variant="h4" component={"h1"}>{t("base.pages.applications")}</Typography>
                <Box sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {availableHostSystems.map((system) => (
                            <Chip
                                key={`host-system-chip-${system.id}`}
                                label={system.name}
                                color={selectedHostSystems.includes(system.id) ? "primary" : "default"}
                                variant={selectedHostSystems.includes(system.id) ? "filled" : "outlined"}
                                onClick={() => toggleHostSystem(system.id)}
                            />
                        ))}
                    </Stack>
                </Box>
                <List>
                    {fileTypeData?.map((fileType) => {
                        const latestFiles = resolveLatestFiles(fileType);
                        return (
                        <ListItem
                            key={fileType.id}
                            disablePadding
                        >
                            <ListItemButton component={Link} to={`${fileType.id}${buildHostSystemSearch(selectedHostSystems)}`} sx={{ gap: 2, alignItems: "center" }}>
                                <ListItemAvatar>
                                    <IconPlaceholder size={40} iconId={fileType.iconId} />
                                </ListItemAvatar>
                                <ListItemText 
                                    primary={fileType.name}
                                    sx={{ flex: "1 1 auto", minWidth: 0 }}
                                />
                                <Box sx={{ flex: "0 0 auto", display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "flex-end", maxWidth: { xs: "100%", md: "60%" } }}>
                                    {latestFiles.length > 0 ? (
                                        latestFiles.map((latestFile, index) => (
                                            <Chip
                                                key={`${latestFile.hostSystemId ?? latestFile.hostSystemName ?? index}`}
                                                label={formatLatestFileLine(latestFile)}
                                                size="small"
                                                variant="outlined"
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            {t("applications.noLatestApplication")}
                                        </Typography>
                                    )}
                                </Box>
                            </ListItemButton>
                        </ListItem>
                        );
                    })}
                </List>
            </Paper>
        </Container>
    );
};